import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, Typography, Grid, Card, CardContent, Chip, Button, IconButton,
    TextField, MenuItem, Select, FormControl, InputLabel, Collapse, Divider,
    Paper, Stack, Snackbar, Alert, ToggleButtonGroup, ToggleButton, Tooltip,
    Avatar, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, TableSortLabel, InputAdornment, Badge, Drawer,
    List, ListItem, ListItemText, ListItemIcon, Breadcrumbs, Link,
    CircularProgress, Skeleton, Fade, Zoom, Tabs, Tab, Accordion,
    AccordionSummary, AccordionDetails, Switch, FormControlLabel,
} from "@mui/material";
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Error as ErrorIcon,
    HourglassEmpty as PendingIcon,
    Sync as ProcessingIcon,
    ErrorOutline as FailedIcon,
    Wallet as WalletIcon,
    AccountBalance as BankIcon,
    PhoneAndroid as UpiIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    ExpandMore as ExpandMoreIcon,
    TrendingUp as TrendingUpIcon,
    AttachMoney as MoneyIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    ContentCopy as CopyIcon,
    Download as DownloadIcon,
    ArrowBack as ArrowBackIcon,
    ReceiptLong as ReceiptIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    Assignment as AssignmentIcon,
    Notifications as NotifIcon,
} from "@mui/icons-material";

import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";

const getAllRefunds = async (params = {}) => {
    const res = await apiClient.get("/api/admin/refunds", { params });
    // return res.data;
    console.log(res.data)
    const data = res?.data?.data
    // ── MOCK ──
    // await new Promise((r) => setTimeout(r, 800));
    // return { data: MOCK_REFUNDS, total: MOCK_REFUNDS.length };
    return data;
};

const adminUpdateRefund = async (id, payload) => {
    const res = await apiClient.put(`/api/admin/refunds/${id}`, payload);
    return res.data;
};

// ─── Config ────────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
    pending: { label: "Pending", color: "warning", bgColor: "#fff8e1", textColor: "#e65100", icon: <PendingIcon fontSize="small" /> },
    approved: { label: "Approved", color: "info", bgColor: "#e3f2fd", textColor: "#0d47a1", icon: <CheckCircleIcon fontSize="small" /> },
    rejected: { label: "Rejected", color: "error", bgColor: "#fce4ec", textColor: "#b71c1c", icon: <CancelIcon fontSize="small" /> },
    processing: { label: "Processing", color: "primary", bgColor: "#ede7f6", textColor: "#4527a0", icon: <ProcessingIcon fontSize="small" /> },
    completed: { label: "Completed", color: "success", bgColor: "#e8f5e9", textColor: "#1b5e20", icon: <CheckCircleIcon fontSize="small" /> },
    failed: { label: "Failed", color: "error", bgColor: "#fce4ec", textColor: "#b71c1c", icon: <FailedIcon fontSize="small" /> },
};

const METHOD_CONFIG = {
    wallet: { label: "Wallet", color: "#7c3aed", bgColor: "#f5f3ff", icon: <WalletIcon fontSize="small" /> },
    upi: { label: "UPI", color: "#b45309", bgColor: "#fffbeb", icon: <UpiIcon fontSize="small" /> },
    bank: { label: "Bank", color: "#1e40af", bgColor: "#eff6ff", icon: <BankIcon fontSize="small" /> },
};

const REFUND_TYPE_CONFIG = {
    order_cancel: { label: "Order Cancel", color: "#dc2626" },
    return: { label: "Return", color: "#d97706" },
    manual: { label: "Manual", color: "#7c3aed" },
};

// ─── Formatters ────────────────────────────────────────────────────────────────
const fmtCurrency = (v) =>
    "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtDateTime = (d) =>
    d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const getInitials = (name = "") => name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

// ─── StatusChip ────────────────────────────────────────────────────────────────
function StatusChip({ status, size = "small" }) {
    const cfg = STATUS_CONFIG[status] || { label: status, color: "default" };
    return (
        <Chip
            icon={<Box sx={{ color: `${cfg.textColor} !important`, display: "flex", "& svg": { fontSize: 14 } }}>{cfg.icon}</Box>}
            label={cfg.label}
            size={size}
            sx={{
                background: cfg.bgColor,
                color: cfg.textColor,
                fontWeight: 700,
                fontSize: size === "small" ? "0.7rem" : "0.8rem",
                border: `1px solid ${cfg.textColor}22`,
                "& .MuiChip-icon": { color: `${cfg.textColor} !important` },
            }}
        />
    );
}

// ─── MethodChip ────────────────────────────────────────────────────────────────
function MethodChip({ method }) {
    const cfg = METHOD_CONFIG[method] || { label: method, color: "#555", bgColor: "#eee" };
    return (
        <Chip
            icon={<Box sx={{ color: `${cfg.color} !important`, display: "flex", "& svg": { fontSize: 14 } }}>{cfg.icon}</Box>}
            label={cfg.label}
            size="small"
            sx={{
                background: cfg.bgColor,
                color: cfg.color,
                fontWeight: 700,
                fontSize: "0.7rem",
                border: `1px solid ${cfg.color}33`,
                "& .MuiChip-icon": { color: `${cfg.color} !important` },
            }}
        />
    );
}

// ─── StatCard ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color, icon, trend }) {
    return (
        <Card elevation={0} sx={{ border: "1px solid #e2e8f0", height: "100%", borderRadius: 2, transition: "all .2s", "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,.08)", transform: "translateY(-1px)" } }}>
            <CardContent sx={{ p: "16px 20px !important" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, fontSize: "0.65rem" }}>
                            {label}
                        </Typography>
                        <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: color || "text.primary", lineHeight: 1.1, mt: 0.5 }}>
                            {value}
                        </Typography>
                        {sub && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>{sub}</Typography>}
                    </Box>
                    {icon && (
                        <Box sx={{ p: 1.2, borderRadius: 2, background: `${color}15`, color, display: "flex" }}>{icon}</Box>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

// ─── CopyText ─────────────────────────────────────────────────────────────────
function CopyText({ value }) {
    const [copied, setCopied] = useState(false);
    if (!value) return <Typography variant="body2" color="text.disabled">—</Typography>;
    return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{value}</Typography>
            <Tooltip title={copied ? "Copied!" : "Copy"}>
                <IconButton size="small" onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
                    <CopyIcon sx={{ fontSize: 13, color: copied ? "success.main" : "text.disabled" }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

// ─── Payment Detail Row ────────────────────────────────────────────────────────
function DetailRow({ label, value, mono, copy }) {
    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.75, borderBottom: "1px dashed #f1f5f9" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", minWidth: 130 }}>
                {label}
            </Typography>
            {copy
                ? <CopyText value={value} />
                : <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: mono ? "monospace" : "inherit", fontSize: mono ? "0.8rem" : "inherit" }}>
                    {value || "—"}
                </Typography>
            }
        </Box>
    );
}

// ─── Manage Refund Dialog ──────────────────────────────────────────────────────
function ManageRefundDialog({ refund, open, onClose, onSave, onGet }) {
    const [status, setStatus] = useState(refund?.status || "pending");
    const [adminNote, setAdminNote] = useState(refund?.adminNote || "");
    const [referenceId, setReferenceId] = useState(refund?.referenceId || "");
    // const [refundAt, setRefundAt] = useState(refund?.refund_at ? refund.refund_at.slice(0, 16) : "");
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [tab, setTab] = useState(0);
    const [retunStatus, setRetunStatus] = useState('')

    useEffect(() => {
        if (!refund) return;
        if (refund) {
            setStatus(refund.status || "pending");
            setAdminNote(refund.adminNote || "");
            setReferenceId(refund.referenceId || "");
            // setRefundAt(
            //     refund.refund_at
            //         ? refund.refund_at.slice(0, 16)
            //         : ""
            // );
            setErrors({});
        }
    }, [refund]);


    // 🔥 Fetch Data
    const fetchReturns = async () => {
        try {
            const res = await apiClient.get("api/user/order-get/" + refund?.awbCode);
            const shipment =
                res?.data?.data?.data?.tracking_data?.shipment_track?.[0] || {};
            setRetunStatus(shipment?.current_status || '')
        } catch {
            // toast.error("Failed to load shipping status");
        }
    };

    useEffect(() => {
        if (refund?.awbCode) {
            fetchReturns();
        }
    }, [refund?.awbCode]);

    // const validate = () => {
    //     const e = {};
    //     if (["completed", "processing"].includes(status) && !referenceId.trim())
    //         e.referenceId = "Reference ID is required when status is Processing or Completed";
    //     if (status === "rejected" && !adminNote.trim())
    //         e.adminNote = "Admin note is required when rejecting a refund";
    //     setErrors(e);
    //     return Object.keys(e).length === 0;
    // };

    // const handleSave = async () => {
    //     if (!validate()) return;
    //     setSaving(true);
    //     try {
    //         await onSave(refund.id, { status, adminNote, referenceId, refund_at: refundAt || null });
    //         onClose();
    //     } finally {
    //         setSaving(false);
    //     }
    // };

    const handleSave = async () => {

        const e = {};

        // ─────────────────────────────
        // VALIDATIONS
        // ─────────────────────────────

        if (!status) {
            e.status = "Status is required";
        }

        // Processing / completed needs transaction reference
        if (
            ["completed", 'failed'].includes(status) &&
            !referenceId?.trim()
        ) {
            e.referenceId =
                "Reference / Transaction ID is required";
        }

        // Rejected needs note
        if (
            status === "rejected" &&
            !adminNote?.trim()
        ) {
            e.adminNote =
                "Rejection reason is required";
        }

        // Failed needs note
        if (
            status === "failed" &&
            !adminNote?.trim()
        ) {
            e.adminNote =
                "Failure reason is required";
        }

        setErrors(e);

        if (Object.keys(e).length > 0) {
            return;
        }

        // ─────────────────────────────
        // PAYLOAD
        // ─────────────────────────────

        const payload = {
            status,
            adminNote: adminNote?.trim() || null,
            referenceId: referenceId?.trim() || null,
        };

        // Auto refund time
        if (status === "completed") {
            payload.refund_at = new Date();
        }

        setSaving(true);

        try {

            await adminUpdateRefund(refund.id, payload);

            toast.success(
                status === "approved"
                    ? "Refund approved successfully"
                    : status === "processing"
                        ? "Refund moved to processing"
                        : status === "completed"
                            ? "Refund completed successfully"
                            : status === "rejected"
                                ? "Refund rejected successfully"
                                : status === "failed"
                                    ? "Refund marked as failed"
                                    : "Refund updated successfully"
            );

            onClose();
            onGet()
        } catch (error) {

            toast.error(
                error?.response?.data?.message ||
                "Failed to update refund"
            );

        } finally {
            setSaving(false);
        }
    };

    //     useEffect(() => {

    //     if (status === "approved" && !adminNote) {
    //         setAdminNote("Refund approved by admin");
    //     }

    //     if (status === "processing" && !adminNote) {
    //         setAdminNote("Refund is under processing");
    //     }

    //     if (status === "completed" && !adminNote) {
    //         setAdminNote("Refund completed successfully");
    //     }

    //     if (status === "rejected" && !adminNote) {
    //         setAdminNote("Refund rejected by admin");
    //     }

    //     if (status === "failed" && !adminNote) {
    //         setAdminNote("Refund transaction failed");
    //     }

    // }, [status]);

    const isCompleted = refund?.status === "completed";
    const allowedTransitions = {
        pending: ["approved", "rejected"],
        approved: ["processing", "rejected"],
        processing: ["completed", "failed"],
        failed: ["processing"],
        completed: [],
        rejected: [],
    };

    const canMoveTo = (nextStatus) => {
        return allowedTransitions[refund.status]?.includes(nextStatus);
    };

    const hasActions =
        allowedTransitions[refund?.status]?.length > 0;

    if (!refund) return null;
    const m = refund.method;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pb: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight={800}>Manage Refund </Typography>
                      {refund?.awbCode &&  <Typography variant="h6" >AWB: {refund?.awbCode} {retunStatus} </Typography>}
                        <Typography variant="caption" color="text.secondary">{refund.order?.orderNumber} · {refund.user?.name}</Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="h5" fontWeight={800} color="primary">{fmtCurrency(refund.amount)}</Typography>
                        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                    </Stack>
                </Stack>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                    <Tab label="Actions" sx={{ fontWeight: 700, fontSize: "0.8rem" }} />
                    <Tab label="User & Payment Info" sx={{ fontWeight: 700, fontSize: "0.8rem" }} />
                </Tabs>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <br />
                {/* Tab 0: Admin Actions */}
                {tab === 0 && (
                    <Grid container spacing={2.5} sx={{ mt: 0 }}>
                        {/* Current status summary */}
                        <Grid item size={{ xs: 12 }}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, background: "#f8fafc" }}>
                                <Grid container spacing={2}>
                                    {[
                                        { label: "Order", value: refund.order?.orderNumber },
                                        { label: "Type", value: <Chip label={REFUND_TYPE_CONFIG[refund.refundType]?.label || refund.refundType} size="small" sx={{ fontWeight: 700, fontSize: "0.7rem" }} /> },
                                        { label: "Method", value: <MethodChip method={refund.method} /> },
                                        { label: "Current Status", value: <StatusChip status={refund.status} /> },
                                        { label: "Requested", value: fmtDateTime(refund.createdAt) },
                                        refund.reason && { label: "Reason", value: refund.reason },
                                        refund.adminNote && { label: "Admin Note", value: refund.adminNote },
                                        refund.referenceId && { label: "ReferenceId", value: refund.referenceId },
                                    ].filter(Boolean).map((res) => (
                                        <Grid item size={{ xs: 12, sm: 6 }} key={res.label}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 0.5 }}>
                                                {res.label}
                                            </Typography>
                                            {typeof res.value === "string"
                                                ? <Typography variant="body2" fontWeight={600}>{res.value}</Typography>
                                                : res.value}
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Status selector */}
                        {/* <Grid item size={{ xs: 12 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: "text.secondary", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
                                Update Status
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                    <Button
                                        key={key}
                                        size="small"
                                        variant={status === key ? "contained" : "outlined"}
                                        startIcon={cfg.icon}
                                        onClick={() => setStatus(key)}
                                        color={cfg.color}
                                        sx={{ fontWeight: 700, fontSize: "0.72rem", borderRadius: 2 }}
                                    >
                                        {cfg.label}
                                    </Button>
                                ))}
                            </Stack>
                        </Grid> */}

                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>

                            {Object.entries(STATUS_CONFIG)
                                .filter(([key]) => canMoveTo(key))
                                .map(([key, cfg]) => (

                                    <Button
                                        key={key}
                                        size="small"
                                        variant={status === key ? "contained" : "outlined"}
                                        startIcon={cfg.icon}
                                        onClick={() => setStatus(key)}
                                        color={cfg.color}
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: "0.72rem",
                                            borderRadius: 2,
                                        }}
                                    >
                                        {cfg.label}
                                    </Button>
                                ))}
                        </Stack>
                        <br />
                        {!isCompleted && <>
                            {/* Reference ID */}
                            {(status === 'failed' || status === 'completed') &&
                                <Grid item size={{ xs: 12, sm: 12 }}>
                                    <TextField
                                        fullWidth size="small" label="Reference / Transaction ID"
                                        placeholder="e.g. UTR1234567890 or TXN-ABCDE"
                                        value={referenceId}
                                        onChange={(e) => setReferenceId(e.target.value)}
                                        error={!!errors.referenceId}
                                        helperText={errors.referenceId || (["completed", "failed"].includes(status) ? "Required for this status" : "Optional – enter after initiating transfer")}
                                        InputProps={{
                                            style: { fontFamily: "monospace", fontSize: "0.85rem" },
                                            startAdornment: <InputAdornment position="start"><ReceiptIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment>,
                                        }}
                                    />
                                </Grid>
                            }

                            {/* Refund Date */}
                            {/* <Grid item size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth size="small" label="Refund Date & Time"
                                type="datetime-local"
                                value={refundAt}
                                onChange={(e) => setRefundAt(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="When was the refund actually processed?"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment>,
                                }}
                            />
                        </Grid> */}

                            {/* Admin Note */}
                            <Grid item size={{ xs: 12 }}>
                                <TextField
                                    fullWidth multiline rows={3} size="small"
                                    label="Admin Note (internal)"
                                    placeholder="Internal note – rejection reason, observations, follow-up actions…"
                                    value={adminNote}
                                    onChange={(e) => setAdminNote(e.target.value)}
                                    error={!!errors.adminNote}
                                    helperText={errors.adminNote || (status === "rejected" ? "Required when rejecting a refund" : "Optional internal comment")}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 0.5 }}><AssignmentIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment>,
                                    }}
                                />
                            </Grid>
                        </>}


                        {/* Quick Action Buttons */}
                        {/* <Grid item size={{ xs: 12 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1.5 }}>
                                Quick Actions
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                <Button size="small" variant="contained" color="info" startIcon={<CheckCircleIcon />}
                                    onClick={() => { setStatus("approved"); }} sx={{ fontWeight: 700 }}>
                                    Approve
                                </Button>
                                <Button size="small" variant="contained" color="primary" startIcon={<ProcessingIcon />}
                                    onClick={() => { setStatus("processing"); }} sx={{ fontWeight: 700 }}>
                                    Mark Processing
                                </Button>
                                <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
                                    onClick={() => { setStatus("completed"); }} sx={{ fontWeight: 700 }}>
                                    Mark Completed
                                </Button>
                                <Button size="small" variant="contained" color="error" startIcon={<CancelIcon />}
                                    onClick={() => { setStatus("rejected"); }} sx={{ fontWeight: 700 }}>
                                    Reject
                                </Button>
                            </Stack>
                        </Grid> */}

                        {/* <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>

                            {canMoveTo("approved") && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="info"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => setStatus("approved")}
                                    sx={{ fontWeight: 700 }}
                                >
                                    Approve
                                </Button>
                            )}

                            {canMoveTo("processing") && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<ProcessingIcon />}
                                    onClick={() => setStatus("processing")}
                                    sx={{ fontWeight: 700 }}
                                >
                                    Mark Processing
                                </Button>
                            )}

                            {canMoveTo("completed") && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => setStatus("completed")}
                                    sx={{ fontWeight: 700 }}
                                >
                                    Mark Completed
                                </Button>
                            )}

                            {canMoveTo("failed") && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="warning"
                                    startIcon={<ErrorIcon />}
                                    onClick={() => setStatus("failed")}
                                    sx={{ fontWeight: 700 }}
                                >
                                    Mark Failed
                                </Button>
                            )}

                            {canMoveTo("rejected") && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="error"
                                    startIcon={<CancelIcon />}
                                    onClick={() => setStatus("rejected")}
                                    sx={{ fontWeight: 700 }}
                                >
                                    Reject
                                </Button>
                            )}

                        </Stack> */}
                    </Grid>
                )}

                {/* Tab 1: User & Payment */}
                {tab === 1 && (
                    <Grid container spacing={2.5} sx={{ mt: 0 }}>
                        {/* User Info */}
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                    <Avatar sx={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", fontWeight: 700, fontSize: "0.9rem" }}>
                                        {getInitials(refund.user?.name)}
                                    </Avatar>
                                    <Box>
                                        <Typography fontWeight={700}>{refund.user?.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{refund.user?.email}</Typography>
                                    </Box>
                                </Stack>
                                <Divider sx={{ mb: 2 }} />
                                <DetailRow label="Phone" value={refund.user?.mobile} />
                                <DetailRow label="User ID" value={refund.userId} mono copy />
                                <DetailRow label="Order Number" value={refund.order?.orderNumber} />
                                {/* <DetailRow label="Order Total" value={fmtCurrency(refund.order?.total)} /> */}
                                <DetailRow label="Refund Amount" value={fmtCurrency(refund.amount)} />
                            </Paper>
                        </Grid>

                        {/* Payment Details */}
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                    <Box sx={{ p: 0.8, borderRadius: 1.5, background: METHOD_CONFIG[m]?.bgColor }}>
                                        <Box sx={{ color: METHOD_CONFIG[m]?.color, display: "flex" }}>{METHOD_CONFIG[m]?.icon}</Box>
                                    </Box>
                                    <Typography fontWeight={700}>{METHOD_CONFIG[m]?.label} Details</Typography>
                                </Stack>
                                <Divider sx={{ mb: 2 }} />

                                {m === "wallet" && (
                                    <Box sx={{ textAlign: "center", py: 3 }}>
                                        <WalletIcon sx={{ fontSize: 40, color: "#7c3aed", mb: 1 }} />
                                        <Typography fontWeight={700} color="#7c3aed">Wallet Refund</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Amount will be credited to user's wallet balance
                                        </Typography>
                                    </Box>
                                )}

                                {m === "upi" && (
                                    <>
                                        <DetailRow label="UPI ID" value={refund.upiId} mono copy />
                                        <DetailRow label="Amount" value={fmtCurrency(refund.amount)} />
                                        {refund.referenceId && <DetailRow label="UTR / Ref ID" value={refund.referenceId} mono copy />}
                                        {refund.refund_at && <DetailRow label="Refunded On" value={fmtDateTime(refund.refund_at)} />}
                                    </>
                                )}

                                {m === "bank" && (
                                    <>
                                        <DetailRow label="Account Holder" value={refund.accountHolderName} />
                                        <DetailRow label="Bank Name" value={refund.bankName} />
                                        <DetailRow label="Account Number" value={refund.accountNumber} mono copy />
                                        <DetailRow label="IFSC Code" value={refund.ifscCode} mono copy />
                                        <DetailRow label="Branch" value={refund.branchName} />
                                        {refund.referenceId && <DetailRow label="Reference ID" value={refund.referenceId} mono copy />}
                                        {refund.refund_at && <DetailRow label="Refunded On" value={fmtDateTime(refund.refund_at)} />}
                                    </>
                                )}
                            </Paper>
                        </Grid>

                        {/* Timeline */}
                        <Grid item size={{ xs: 12 }}>
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Refund Timeline</Typography>
                                <Stack spacing={1.5}>
                                    {[
                                        { label: "Refund Requested", date: refund.createdAt, done: true },
                                        // { label: "Status: Approved", date: ["approved", "processing", "completed"].includes(refund.status) ? refund.updatedAt : null, done: ["approved", "processing", "completed"].includes(refund.status) },
                                        // { label: "Processing Initiated", date: ["processing", "completed"].includes(refund.status) ? refund.updatedAt : null, done: ["processing", "completed"].includes(refund.status) },
                                        { label: "Refund Completed", date: refund.refund_at, done: refund.status === "completed" },
                                    ].map((step, i) => (
                                        <Stack key={i} direction="row" spacing={2} alignItems="center">
                                            <Box sx={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: step.done ? "#dcfce7" : "#f1f5f9", border: `2px solid ${step.done ? "#22c55e" : "#cbd5e1"}` }}>
                                                {step.done ? <CheckCircleIcon sx={{ fontSize: 14, color: "#22c55e" }} /> : <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#cbd5e1" }} />}
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" fontWeight={step.done ? 700 : 400} color={step.done ? "text.primary" : "text.disabled"}>
                                                    {step.label}
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">{fmtDateTime(step.date)}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit" sx={{ fontWeight: 700 }}>Cancel</Button>
                {/* <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    color={status === "rejected" ? "error" : status === "completed" ? "success" : "primary"}
                    sx={{ fontWeight: 700, minWidth: 160 }}
                >
                    {saving ? "Saving…" : status === "rejected" ? "Reject Refund" : status === "completed" ? "Mark Completed" : status === "approved" ? "Approve Refund" : "Save Changes"}
                </Button> */}
                {hasActions && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving}
                        startIcon={
                            saving
                                ? <CircularProgress size={16} color="inherit" />
                                : <SaveIcon />
                        }
                        color={
                            status === "rejected"
                                ? "error"
                                : status === "completed"
                                    ? "success"
                                    : "primary"
                        }
                        sx={{ fontWeight: 700, minWidth: 160 }}
                    >
                        {saving
                            ? "Saving…"
                            : status === "rejected"
                                ? "Reject Refund"
                                : status === "completed"
                                    ? "Mark Completed"
                                    : status === "approved"
                                        ? "Approve Refund"
                                        : "Save Changes"}
                    </Button>
                )}

            </DialogActions>
        </Dialog>
    );
}

// ─── View Drawer ────────────────────────────────────────────────────────────────
// function RefundViewDrawer({ refund, open, onClose, onManage }) {
//     if (!refund) return null;
//     const m = refund.method;

//     return (
//         <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 440 }, p: 0 } }}>
//             {/* Header */}
//             <Box sx={{ p: 2.5, borderBottom: "1px solid #e2e8f0", background: "linear-gradient(135deg,#f8fafc,#f1f5f9)" }}>
//                 <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
//                     <Box>
//                         <Typography variant="h6" fontWeight={800}>{refund.order?.orderNumber}</Typography>
//                         <Typography variant="caption" color="text.secondary">{fmtDateTime(refund.createdAt)}</Typography>
//                     </Box>
//                     <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
//                 </Stack>
//                 <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
//                     <Avatar sx={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", width: 44, height: 44, fontWeight: 700 }}>
//                         {getInitials(refund.user?.name)}
//                     </Avatar>
//                     <Box>
//                         <Typography fontWeight={700}>{refund.user?.name}</Typography>
//                         <Typography variant="caption" color="text.secondary">{refund.user?.email}</Typography>
//                     </Box>
//                 </Stack>
//             </Box>

//             {/* Amount + Status */}
//             <Box sx={{ px: 2.5, py: 2, background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center">
//                     <Box>
//                         <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Refund Amount</Typography>
//                         <Typography sx={{ fontSize: "1.8rem", fontWeight: 900, color: "primary.main", lineHeight: 1.2 }}>{fmtCurrency(refund.amount)}</Typography>
//                     </Box>
//                     <Box sx={{ textAlign: "right" }}>
//                         <StatusChip status={refund.status} />
//                         <Box sx={{ mt: 0.5 }}><MethodChip method={refund.method} /></Box>
//                     </Box>
//                 </Stack>
//             </Box>

//             {/* Details */}
//             <Box sx={{ flex: 1, overflow: "auto", p: 2.5 }}>
//                 <Stack spacing={2.5}>
//                     {/* Refund Info */}
//                     <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
//                         <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Refund Info</Typography>
//                         <DetailRow label="Refund Type" value={<Chip label={REFUND_TYPE_CONFIG[refund.refundType]?.label || refund.refundType} size="small" sx={{ fontWeight: 700, fontSize: "0.68rem" }} />} />
//                         <DetailRow label="User Reason" value={refund.reason} />
//                         {refund.adminNote && <DetailRow label="Admin Note" value={refund.adminNote} />}
//                         {refund.referenceId && <DetailRow label="Reference ID" value={refund.referenceId} mono copy />}
//                         {refund.refund_at && <DetailRow label="Refunded On" value={fmtDateTime(refund.refund_at)} />}
//                     </Paper>

//                     {/* Payment Details */}
//                     <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
//                         <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
//                             <Box sx={{ color: METHOD_CONFIG[m]?.color, display: "flex" }}>{METHOD_CONFIG[m]?.icon}</Box>
//                             <Typography variant="subtitle2" fontWeight={700}>{METHOD_CONFIG[m]?.label} Details</Typography>
//                         </Stack>

//                         {m === "wallet" && (
//                             <Typography variant="body2" color="text.secondary">Amount credited directly to user's wallet.</Typography>
//                         )}
//                         {m === "upi" && <>
//                             <DetailRow label="UPI ID" value={refund.upiId} mono copy />
//                         </>}
//                         {m === "bank" && <>
//                             <DetailRow label="Account Holder" value={refund.accountHolderName} />
//                             <DetailRow label="Bank" value={refund.bankName} />
//                             <DetailRow label="Account No." value={refund.accountNumber} mono copy />
//                             <DetailRow label="IFSC" value={refund.ifscCode} mono copy />
//                             <DetailRow label="Branch" value={refund.branchName} />
//                         </>}
//                     </Paper>
//                 </Stack>
//             </Box>

//             {/* Footer */}
//             <Box sx={{ p: 2.5, borderTop: "1px solid #e2e8f0" }}>
//                 <Button fullWidth variant="contained" size="large" startIcon={<EditIcon />} onClick={() => { onClose(); onManage(refund); }} sx={{ fontWeight: 700, borderRadius: 2 }}>
//                     Manage Refund
//                 </Button>
//             </Box>
//         </Drawer>
//     );
// }

// ─── Bulk Action Bar ──────────────────────────────────────────────────────────
// function BulkActionBar({ selected, onClearSelection, onBulkAction }) {
//     if (selected.length === 0) return null;
//     return (
//         <Fade in>
//             <Paper elevation={4} sx={{ position: "sticky", top: 12, zIndex: 10, p: 1.5, borderRadius: 2, background: "#1e293b", color: "#fff", display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
//                 <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, flex: 1 }}>
//                     {selected.length} refund{selected.length > 1 ? "s" : ""} selected
//                 </Typography>
//                 <Button size="small" variant="contained" color="info" onClick={() => onBulkAction("approved")} sx={{ fontWeight: 700, color: "#fff" }}>Approve All</Button>
//                 <Button size="small" variant="contained" color="success" onClick={() => onBulkAction("completed")} sx={{ fontWeight: 700 }}>Complete All</Button>
//                 <Button size="small" variant="contained" color="error" onClick={() => onBulkAction("rejected")} sx={{ fontWeight: 700 }}>Reject All</Button>
//                 <IconButton size="small" onClick={onClearSelection} sx={{ color: "#94a3b8" }}><CloseIcon fontSize="small" /></IconButton>
//             </Paper>
//         </Fade>
//     );
// }

// ─── Main Admin Page ──────────────────────────────────────────────────────────
export default function AdminRefundManagement() {
    const [refunds, setRefunds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterMethod, setFilterMethod] = useState("");
    const [filterType, setFilterType] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState("createdAt");
    const [orderDir, setOrderDir] = useState("desc");
    const [selected, setSelected] = useState([]);
    // const [viewRefund, setViewRefund] = useState(null);
    const [manageRefund, setManageRefund] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });
    const [totalCount, setTotalCount] = useState(0);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        processing: 0,
        completed: 0,
        rejected: 0,
        totalAmt: 0,
        completedAmt: 0,
        pendingAmt: 0,
        walletAmt: 0,
        upiAmt: 0,
        bankAmt: 0,
    });

    // const toast = useCallback((msg, severity = "success") => {
    //     setSnackbar({ open: true, msg, severity });
    // }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAllRefunds({
                search,
                page: page + 1,
                limit: rowsPerPage,
                status: filterStatus,
                method: filterMethod,
                refundType: filterType,
            });
            setRefunds(res.data || []);
            setTotalCount(res?.total || 0);

            setStats({
                total: Number(res?.stats?.totalRefunds || 0),
                pending: Number(res?.stats?.pendingCount || 0),
                approved: Number(res?.stats?.approvedCount || 0),
                processing: Number(res?.stats?.processingCount || 0),
                completed: Number(res?.stats?.completedCount || 0),
                rejected: Number(res?.stats?.rejectedCount || 0),
                totalAmt: Number(res?.stats?.totalAmount || 0),
                completedAmt: Number(res?.stats?.completedAmount || 0),
                pendingAmt: Number(res?.stats?.pendingAmount || 0),
                walletAmt: Number(res?.stats?.walletAmount || 0),
                upiAmt: Number(res?.stats?.upiAmount || 0),
                bankAmt: Number(res?.stats?.bankAmount || 0),
            });
        } catch (err) {
            toast.error("Failed to load refunds");
        } finally {
            setLoading(false);
        }
    }, [search, page, filterStatus, rowsPerPage, filterType, filterMethod]);

    // userId,

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Stats ──
    // const stats = useMemo(() => {
    //     const total = refunds.reduce((a, r) => a + Number(r.amount), 0);
    //     const completed = refunds.filter((r) => r.status === "completed");
    //     return {
    //         total: refunds.length,
    //         pending: refunds.filter((r) => r.status === "pending").length,
    //         approved: refunds.filter((r) => r.status === "approved").length,
    //         processing: refunds.filter((r) => r.status === "processing").length,
    //         completed: completed.length,
    //         rejected: refunds.filter((r) => r.status === "rejected").length,
    //         totalAmt: total,
    //         completedAmt: completed.reduce((a, r) => a + Number(r.amount), 0),
    //         pendingAmt: refunds.filter((r) => r.status === "pending").reduce((a, r) => a + Number(r.amount), 0),
    //         walletAmt: refunds.filter((r) => r.method === "wallet").reduce((a, r) => a + Number(r.amount), 0),
    //         upiAmt: refunds.filter((r) => r.method === "upi").reduce((a, r) => a + Number(r.amount), 0),
    //         bankAmt: refunds.filter((r) => r.method === "bank").reduce((a, r) => a + Number(r.amount), 0),
    //     };
    // }, [refunds]);

    // ── Filtered & Sorted ──
    const filtered = useMemo(() => {
        let list = [...refunds];
        list.sort((a, b) => {
            let av = a[orderBy], bv = b[orderBy];
            if (orderBy === "amount") { av = Number(av); bv = Number(bv); }
            if (av < bv) return orderDir === "asc" ? -1 : 1;
            if (av > bv) return orderDir === "asc" ? 1 : -1;
            return 0;
        });
        return list;
    }, [refunds, orderBy, orderDir]);

    const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleSort = (col) => {
        if (orderBy === col) setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setOrderBy(col); setOrderDir("desc"); }
    };

    // const handleSelect = (id) =>
    //     setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

    // const handleSelectAll = (e) =>
    //     setSelected(e.target.checked ? paginated.map((r) => r.id) : []);

    // const handleSaveRefund = async (id, data) => {
    //     try {
    //         await adminUpdateRefund(id, data);
    //         setRefunds((prev) => prev.map((r) => r.id === id ? { ...r, ...data } : r));
    //         toast.success("Refund updated successfully!");
    //         setSelected([]);
    //     } catch {
    //         toast.error("Failed to update refund");
    //     }
    // };

    // const handleBulkAction = async (newStatus) => {
    //     try {
    //         await Promise.all(selected.map((id) => adminUpdateRefund(id, { status: newStatus })));
    //         setRefunds((prev) => prev.map((r) => selected.includes(r.id) ? { ...r, status: newStatus } : r));
    //         toast(`${selected.length} refunds updated to ${newStatus}`);
    //         setSelected([]);
    //     } catch {
    //         toast("Bulk action failed", "error");
    //     }
    // };

    const columns = [
        { id: "order", label: "Order / User", sortKey: null },
        { id: "amount", label: "Amount", sortKey: "amount" },
        { id: "refundType", label: "Type", sortKey: null },
        { id: "method", label: "Method", sortKey: null },
        { id: "status", label: "Status", sortKey: "status" },
        { id: "referenceId", label: "Reference ID", sortKey: null },
        { id: "createdAt", label: "Requested", sortKey: "createdAt" },
        { id: "actions", label: "Actions", sortKey: null },
    ];

    return (
        <Box>
            {/* Page Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: "-0.5px" }}>
                        Refund Management
                    </Typography>
                    {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage, approve, reject, and track all refund requests
                    </Typography> */}
                </Box>
                {/* <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchData} disabled={loading} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
                            {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
                        </IconButton>
                    </Tooltip>
                    <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ fontWeight: 700, borderRadius: 2 }}>
                        Export CSV
                    </Button>
                </Stack> */}
            </Stack>

            {/* Stats Row */}
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                    { label: "Total Refunds", value: stats.total, sub: fmtCurrency(stats.totalAmt) + " total", color: "#6366f1", icon: <ReceiptIcon /> },
                    { label: "Pending", value: stats.pending, sub: fmtCurrency(stats.pendingAmt) + " at risk", color: "#f59e0b", icon: <PendingIcon /> },
                    { label: "Approved", value: stats.approved, sub: "Ready to process", color: "#3b82f6", icon: <CheckCircleIcon /> },
                    { label: "Processing", value: stats.processing, sub: "In transit", color: "#8b5cf6", icon: <ProcessingIcon /> },
                    { label: "Completed", value: stats.completed, sub: fmtCurrency(stats.completedAmt) + " paid", color: "#22c55e", icon: <CheckCircleIcon /> },
                    { label: "Rejected", value: stats.rejected, sub: "Declined", color: "#ef4444", icon: <CancelIcon /> },
                ].map((s) => (
                    <Grid item size={{ xs: 6, sm: 4, md: 2 }} key={s.label}>
                        <StatCard {...s} />
                    </Grid>
                ))}
            </Grid>

            {/* Breakdown Bar */}
            <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, mb: 3 }}>
                <CardContent sx={{ p: "16px 20px !important" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={700}>Refund Breakdown by Method</Typography>
                    </Stack>
                    <Grid container spacing={2}>
                        {[
                            { method: "wallet", total: stats.walletAmt },
                            { method: "upi", total: stats.upiAmt },
                            { method: "bank", total: stats.bankAmt },
                        ].map(({ method, total }) => {
                            const cfg = METHOD_CONFIG[method];
                            const pct = stats.totalAmt ? Math.round((total / stats.totalAmt) * 100) : 0;
                            return (
                                <Grid item size={{ xs: 12, sm: 4 }} key={method}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                                        <MethodChip method={method} />
                                        <Typography variant="body2" fontWeight={800}>{fmtCurrency(total)}</Typography>
                                    </Stack>
                                    <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, background: "#f1f5f9", "& .MuiLinearProgress-bar": { background: cfg.color, borderRadius: 3 } }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>{pct}% of total refunds</Typography>
                                </Grid>
                            );
                        })}
                    </Grid>
                </CardContent>
            </Card>

            {/* Bulk Action Bar */}
            {/* <BulkActionBar selected={selected} onClearSelection={() => setSelected([])} onBulkAction={handleBulkAction} /> */}

            {/* Filters */}
            <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, mb: 2, overflow: "hidden" }}>
                <Box sx={{ p: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                    {/* Search */}
                    <TextField
                        size="small" placeholder="Search by name, order, email, UPI, reference…"
                        value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} /></InputAdornment> }}
                        sx={{ flex: "1 1 260px", minWidth: 200 }}
                    />

                    {/* Status Filter */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
                            <MenuItem value="">All Statuses</MenuItem>
                            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                                <MenuItem key={k} value={k}><Stack direction="row" spacing={1} alignItems="center"><StatusChip status={k} /><Typography variant="caption" fontWeight={600}>
                                    {/* {refunds.filter(r => r.status === k).length} */}
                                </Typography></Stack></MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Method Filter */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Method</InputLabel>
                        <Select value={filterMethod} label="Method" onChange={(e) => { setFilterMethod(e.target.value); setPage(0); }}>
                            <MenuItem value="">All Methods</MenuItem>
                            {Object.entries(METHOD_CONFIG).map(([k, v]) => (
                                <MenuItem key={k} value={k}><Stack direction="row" spacing={1} alignItems="center"><MethodChip method={k} /></Stack></MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Type Filter */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Type</InputLabel>
                        <Select value={filterType} label="Type" onChange={(e) => { setFilterType(e.target.value); setPage(0); }}>
                            <MenuItem value="">All Types</MenuItem>
                            {Object.entries(REFUND_TYPE_CONFIG).map(([k, v]) => (
                                <MenuItem key={k} value={k}>{v.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* <Typography variant="caption" color="text.secondary" sx={{ ml: "auto", flexShrink: 0, fontWeight: 600 }}>
                        {filtered.length} of {refunds.length} records
                    </Typography> */}
                </Box>

                {/* Quick Status Tabs */}
                {/* <Box sx={{ borderTop: "1px solid #f1f5f9", background: "#f8fafc", px: 1 }}>
                    <ToggleButtonGroup value={filterStatus} exclusive onChange={(_, v) => v && (setFilterStatus(v), setPage(0))} sx={{ flexWrap: "wrap", "& .MuiToggleButtonGroup-grouped": { border: "none", borderRadius: "0 !important", m: 0 } }}>
                        {[["all", `All (${refunds.length})`], ["pending", `Pending (${stats.pending})`], ["approved", `Approved (${stats.approved})`], ["processing", `Processing (${stats.processing})`], ["completed", `Completed (${stats.completed})`], ["rejected", `Rejected (${stats.rejected})`]].map(([k, l]) => (
                            <ToggleButton key={k} value={k} size="small" sx={{ fontSize: "0.72rem", fontWeight: 700, px: 1.5, py: 0.75, textTransform: "none" }}>{l}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box> */}
            </Paper>

            {/* Table */}
            <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ background: "#f8fafc" }}>
                                {/* <TableCell padding="checkbox" sx={{ pl: 2 }}>
                                    <input type="checkbox" checked={selected.length === paginated.length && paginated.length > 0} onChange={handleSelectAll} style={{ cursor: "pointer" }} />
                                </TableCell> */}
                                {columns.map((col) => (
                                    <TableCell key={col.id} sx={{ fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", whiteSpace: "nowrap", py: 1.5 }}>
                                        {col.sortKey ? (
                                            <TableSortLabel active={orderBy === col.sortKey} direction={orderBy === col.sortKey ? orderDir : "asc"} onClick={() => handleSort(col.sortKey)}>
                                                {col.label}
                                            </TableSortLabel>
                                        ) : col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        {Array.from({ length: 9 }).map((_, j) => (
                                            <TableCell key={j}><Skeleton height={20} /></TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : paginated.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ textAlign: "center", py: 8 }}>
                                        <ReceiptIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1.5, display: "block", mx: "auto" }} />
                                        <Typography variant="h6" color="text.secondary">No refunds found</Typography>
                                        <Typography variant="body2" color="text.disabled">Try adjusting your search or filters</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : paginated.map((refund, idx) => (
                                <TableRow
                                    key={refund.id}
                                    hover
                                    selected={selected.includes(refund.id)}
                                    sx={{ cursor: "pointer", "&:last-child td": { border: 0 }, "&.Mui-selected": { background: "#f0f9ff" } }}
                                    onClick={() => setManageRefund(refund)}
                                >
                                    {/* <TableCell padding="checkbox" sx={{ pl: 2 }} onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={selected.includes(refund.id)} onChange={() => handleSelect(refund.id)} style={{ cursor: "pointer" }} />
                                    </TableCell> */}

                                    {/* Order / User */}
                                    <TableCell sx={{ py: 1.5 }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar sx={{ width: 34, height: 34, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", fontSize: "0.72rem", fontWeight: 700, flexShrink: 0 }}>
                                                {getInitials(refund.user?.name)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>{refund.order?.orderNumber}</Typography>
                                                <Typography variant="caption" color="text.secondary">{refund.user?.name}</Typography>
                                                <Typography variant="caption" color="text.disabled" sx={{ display: "block", fontSize: "0.65rem" }}>{refund.user?.email}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>

                                    {/* Amount */}
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={800} color="primary" sx={{ fontFamily: "monospace", fontSize: "0.9rem" }}>
                                            {fmtCurrency(refund.amount)}
                                        </Typography>
                                    </TableCell>

                                    {/* Type */}
                                    <TableCell>
                                        <Typography variant="caption" fontWeight={700} sx={{ color: REFUND_TYPE_CONFIG[refund.refundType]?.color || "#555", textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: "0.05em" }}>
                                            {REFUND_TYPE_CONFIG[refund.refundType]?.label || refund.refundType}
                                        </Typography>
                                    </TableCell>

                                    {/* Method */}
                                    <TableCell><MethodChip method={refund.method} /></TableCell>

                                    {/* Status */}
                                    <TableCell><StatusChip status={refund.status} /></TableCell>

                                    {/* Reference ID */}
                                    <TableCell>
                                        {refund.referenceId
                                            ? <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.75rem", background: "#f0fdf4", color: "#166534", px: 1, py: 0.3, borderRadius: 1, fontWeight: 700 }}>{refund.referenceId}</Typography>
                                            : <Typography variant="caption" color="text.disabled">—</Typography>}
                                    </TableCell>

                                    {/* Date */}
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">{fmtDate(refund.createdAt)}</Typography>
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Stack direction="row" spacing={0.5}>
                                            {/* <Tooltip title="View Details">
                                                <IconButton size="small" onClick={() => setViewRefund(refund)} sx={{ color: "text.secondary" }}>
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip> */}
                                            <Tooltip title="Manage Refund">
                                                <IconButton size="small" onClick={() => setManageRefund(refund)} sx={{ color: "primary.main" }}>
                                                    <ViewIcon fontSize="small" />
                                                    {/* <EditIcon fontSize="small" /> */}
                                                </IconButton>
                                            </Tooltip>
                                            {/* {refund.status === "pending" && (
                                                <>
                                                    <Tooltip title="Quick Approve">
                                                        <IconButton size="small" onClick={() => handleSaveRefund(refund.id, { status: "approved" })} sx={{ color: "success.main" }}>
                                                            <CheckCircleIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Quick Reject">
                                                        <IconButton size="small" onClick={() => handleSaveRefund(refund.id, { status: "rejected" })} sx={{ color: "error.main" }}>
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )} */}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ borderTop: "1px solid #e2e8f0" }}
                />
            </Paper>

            {/* View Drawer */}
            {/* <RefundViewDrawer
                refund={viewRefund}
                open={!!viewRefund}
                onClose={() => setViewRefund(null)}
                onManage={(r) => { setViewRefund(null); setManageRefund(r); }}
            /> */}

            {/* Manage Dialog */}
            <ManageRefundDialog
                refund={manageRefund}
                open={!!manageRefund}
                onClose={() => setManageRefund(null)}
                // onSave={handleSaveRefund}
                onGet={() => fetchData()}
            />

            {/* Toast */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ fontWeight: 700 }}>
                    {snackbar.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}